import {
  ArcDirection,
  ArcSize,
  PathParameter,
  PathParameterType,
} from 'antibodies/visualization/AntibodyPathLinker';
import {assert} from 'Asserts';
import {assertNever} from 'util/Types';

export interface Point {
  x: number;
  y: number;
}

/** The geometry of a line drawn by AntibodyPathLinker (or by one of the components built on it). */
export interface LinePath {
  // The length of the path.
  length: number;
  // The center of the path's bounding box, in the path's coordinate space. This is what the path is
  // rotated and reflected about when it is drawn.
  center: Point;
  // The point a given distance (from 0 to `length`) along the path, and the unit normal to the path there.
  pointAndNormalAt: (distance: number) => {point: Point; normal: Point};
}

interface PathPiece {
  length: number;
  // `distance` runs from 0 to `length` along the piece.
  pointAndNormalAt: (distance: number) => {point: Point; normal: Point};
}

function linePiece(from: Point, to: Point): PathPiece {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  const direction = length === 0 ? {x: 0, y: 1} : {x: (to.x - from.x) / length, y: (to.y - from.y) / length};
  const normal = {x: direction.y, y: -direction.x};
  return {
    length,
    pointAndNormalAt: (distance) => ({
      point: {x: from.x + direction.x * distance, y: from.y + direction.y * distance},
      normal,
    }),
  };
}

// How many steps an arc is divided into to measure the distance along it. An ellipse's arc length has no
// closed form, so distances along the arc are found from a table of the distances at these steps.
const ARC_LENGTH_STEPS = 256;

/**
 * An elliptical arc from `from` to `to`, following the SVG arc rules for the ellipse (`rx`, `ry`, and its
 * `rotation` in degrees), for choosing which of the two ellipses through the endpoints and which way around
 * it to go, and for scaling up radii that are too short to span the endpoints. See
 * https://www.w3.org/TR/SVG/implnote.html#ArcImplementationNotes.
 */
function arcPiece(
  from: Point,
  to: Point,
  {
    rx,
    ry,
    rotation,
    largeArc,
    clockwise,
  }: {rx: number; ry: number; rotation: number; largeArc: boolean; clockwise: boolean}
): PathPiece {
  assert(from.x !== to.x || from.y !== to.y, 'Path arcs must not start and end at the same point');
  // SVG draws an arc with a radius of zero as a straight line.
  if (rx === 0 || ry === 0) {
    return linePiece(from, to);
  }

  // The ellipse is described by its center and, for the angle `theta` around it, the point
  // center + R(phi) * (rx * cos(theta), ry * sin(theta)), where R(phi) rotates by the ellipse's rotation.
  // The following finds the center and the angles of the arc's ends, by working in the frame where the
  // ellipse is axis-aligned and centered on the midpoint of the endpoints.
  const phi = (rotation * Math.PI) / 180;
  const [cosPhi, sinPhi] = [Math.cos(phi), Math.sin(phi)];
  const half = {x: (from.x - to.x) / 2, y: (from.y - to.y) / 2};
  const fromInFrame = {x: cosPhi * half.x + sinPhi * half.y, y: -sinPhi * half.x + cosPhi * half.y};

  const tooShort = (fromInFrame.x / Math.abs(rx)) ** 2 + (fromInFrame.y / Math.abs(ry)) ** 2;
  const scale = Math.max(1, Math.sqrt(tooShort));
  const [a, b] = [Math.abs(rx) * scale, Math.abs(ry) * scale];

  const squaredRatio =
    (a * a * b * b - a * a * fromInFrame.y ** 2 - b * b * fromInFrame.x ** 2) /
    (a * a * fromInFrame.y ** 2 + b * b * fromInFrame.x ** 2);
  const centerSign = largeArc !== clockwise ? 1 : -1;
  const centerFactor = centerSign * Math.sqrt(Math.max(squaredRatio, 0));
  const centerInFrame = {
    x: (centerFactor * a * fromInFrame.y) / b,
    y: (-centerFactor * b * fromInFrame.x) / a,
  };
  const center = {
    x: cosPhi * centerInFrame.x - sinPhi * centerInFrame.y + (from.x + to.x) / 2,
    y: sinPhi * centerInFrame.x + cosPhi * centerInFrame.y + (from.y + to.y) / 2,
  };

  const startTheta = Math.atan2((fromInFrame.y - centerInFrame.y) / b, (fromInFrame.x - centerInFrame.x) / a);
  const endTheta = Math.atan2((-fromInFrame.y - centerInFrame.y) / b, (-fromInFrame.x - centerInFrame.x) / a);
  const fullTurn = 2 * Math.PI;
  // In SVG's y-down coordinates, increasing angles run clockwise.
  const sweep = clockwise
    ? (((endTheta - startTheta) % fullTurn) + fullTurn) % fullTurn
    : -((((startTheta - endTheta) % fullTurn) + fullTurn) % fullTurn);
  const direction = sweep >= 0 ? 1 : -1;

  // How fast the arc's point moves with `theta`, in the direction of increasing `theta`.
  const velocityAt = (theta: number): Point => ({
    x: -a * Math.sin(theta) * cosPhi - b * Math.cos(theta) * sinPhi,
    y: -a * Math.sin(theta) * sinPhi + b * Math.cos(theta) * cosPhi,
  });

  // distanceAtStep[i] is the distance along the arc to the i-th of equal steps in `theta`, found by adding up
  // the speed (the length of the velocity), which is the rate of change of distance, over the steps.
  const stepTheta = sweep / ARC_LENGTH_STEPS;
  const speedAt = (theta: number): number => {
    const velocity = velocityAt(theta);
    return Math.hypot(velocity.x, velocity.y);
  };
  const distanceAtStep = [0];
  for (let i = 1; i <= ARC_LENGTH_STEPS; i++) {
    const meanSpeed = (speedAt(startTheta + (i - 1) * stepTheta) + speedAt(startTheta + i * stepTheta)) / 2;
    distanceAtStep.push(distanceAtStep[i - 1] + meanSpeed * Math.abs(stepTheta));
  }
  const length = distanceAtStep[ARC_LENGTH_STEPS];

  return {
    length,
    pointAndNormalAt: (distance) => {
      // Find the step that the distance is in, then the angle by interpolating within it.
      let step = 1;
      while (step < ARC_LENGTH_STEPS && distanceAtStep[step] < distance) {
        step++;
      }
      const stepLength = distanceAtStep[step] - distanceAtStep[step - 1];
      const fraction = stepLength === 0 ? 0 : (distance - distanceAtStep[step - 1]) / stepLength;
      const theta = startTheta + (step - 1 + fraction) * stepTheta;

      const velocity = velocityAt(theta);
      const speed = Math.hypot(velocity.x, velocity.y);
      // The direction of travel is the velocity, flipped when `theta` decreases along the arc.
      const tangent = {x: (direction * velocity.x) / speed, y: (direction * velocity.y) / speed};
      return {
        point: {
          x: center.x + a * Math.cos(theta) * cosPhi - b * Math.sin(theta) * sinPhi,
          y: center.y + a * Math.cos(theta) * sinPhi + b * Math.sin(theta) * cosPhi,
        },
        // The normal is the direction of travel turned to the left on screen, as it is for a line.
        normal: {x: tangent.y, y: -tangent.x},
      };
    },
  };
}

// How finely each piece is sampled to find the bounding box of the path.
const BOUNDS_SAMPLES_PER_PIECE = 64;

/**
 * The geometry of the path that AntibodyPathLinker draws for `pathParameters`, which starts at `start`
 * and continues through each parameter relative to the end of the previous one.
 *
 * This exposes function to calculate points and normals along the path, which are used for drawing markers
 * along the path.
 */
export function linePathFromParameters(
  pathParameters: ReadonlyArray<PathParameter>,
  start: Point = {x: 0, y: 0}
): LinePath {
  const pieces: Array<PathPiece> = [];
  let current = start;
  for (const parameter of pathParameters) {
    const next = {x: current.x + parameter.dx, y: current.y + parameter.dy};
    switch (parameter.type) {
      case PathParameterType.LINE:
        pieces.push(linePiece(current, next));
        break;
      case PathParameterType.ARC:
        pieces.push(
          arcPiece(current, next, {
            rx: parameter.rx,
            ry: parameter.ry,
            rotation: parameter.rotation,
            largeArc: parameter.arcSize === ArcSize.LARGE,
            clockwise: parameter.direction === ArcDirection.CLOCKWISE,
          })
        );
        break;
      default:
        assertNever(parameter);
    }
    current = next;
  }

  const length = pieces.reduce((sum, piece) => sum + piece.length, 0);
  const pointAndNormalAt = (distance: number): {point: Point; normal: Point} => {
    let remaining = Math.min(Math.max(distance, 0), length);
    for (const piece of pieces) {
      if (remaining <= piece.length) {
        return piece.pointAndNormalAt(remaining);
      }
      remaining -= piece.length;
    }
    const lastPiece = pieces[pieces.length - 1];
    return lastPiece.pointAndNormalAt(lastPiece.length);
  };

  // Rather than explicitly calculating the center of the bounding box, sample points along the path and
  // use them to find the min and max x and y values.
  const sampled = pieces
    .flatMap((piece) =>
      Array.from({length: BOUNDS_SAMPLES_PER_PIECE + 1}, (_, i) =>
        piece.pointAndNormalAt((piece.length * i) / BOUNDS_SAMPLES_PER_PIECE)
      )
    )
    .map(({point}) => point);
  const xs = sampled.map((p) => p.x);
  const ys = sampled.map((p) => p.y);
  return {
    length,
    center: {x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2},
    pointAndNormalAt,
  };
}
